import { fromJS } from 'immutable';
import { createSelector } from 'reselect';
import mixpanel from '../../utilities/mixpanel';
import { PTOAge } from '../../utilities/date';
import { updateSystemOverview } from './system';
import { setShouldFetchBilling } from './billing';
import { updateUserAccount } from './user';
import { resetCases } from './cases';

// ACTION TYPES
export const CHANGE_SERVICE_CONTRACT = 'customer-portal/app/CHANGE_SERVICE_CONTRACT';
export const CHANGE_DEFAULT_PROSPECT = 'customer-portal/app/CHANGE_DEFAULT_PROSPECT';
export const MOBILE_RATING_PROMPT_DATE = 'customer-portal/app/MOBILE_RATING_PROMPT_DATE';
export const SET_INITIAL_CHART_TYPE = 'customer-portal/app/SET_INITIAL_CHART_TYPE';
export const TOGGLE_SIDE_DRAWER = 'customer-portal/app/TOGGLE_SIDE_DRAWER';
export const VIEWED_ADVANCED_GRAPHS = 'customer-portal/app/VIEWED_ADVANCED_GRAPHS';
export const RESET_VIEWED_ADVANCED_GRAPHS = 'customer-portal/app/RESET_VIEWED_ADVANCED_GRAPHS';

// ACTION CREATORS
export const setMobileRatingPromptDate = () => ({
  type: MOBILE_RATING_PROMPT_DATE,
});

export const toggleSideDrawer = () => ({
  type: TOGGLE_SIDE_DRAWER,
});

export const setInitialChartType = ChartType => ({
  type: SET_INITIAL_CHART_TYPE,
  payload: ChartType,
});

export const changeServiceContract = contractId => ({
  type: CHANGE_SERVICE_CONTRACT,
  payload: contractId,
});

export const changeDefaultProspectId = prospectId => ({
  type: CHANGE_DEFAULT_PROSPECT,
  payload: prospectId,
});

export const viewedAdvancedGraphs = () => ({
  type: VIEWED_ADVANCED_GRAPHS,
});

export const resetViewedAdvancedGraphs = () => ({
  type: RESET_VIEWED_ADVANCED_GRAPHS,
});

export const initialState = fromJS({
  sideDrawerOpen: false,
  initialChartType: null,
  mobileRatingPromptDate: new Date().toISOString(),
  notifications: [],
  notificationsLoaded: false,
  viewedAdvancedGraphs: false,
  defaultProspectId: null,
});

// THUNKS
export const setServiceContract = (account, masterId, userInfo) => (dispatch, getState) => {
  const { system } = getState();

  dispatch(updateSystemOverview(account.prospectId));
  dispatch(updateUserAccount(account));
  dispatch(setShouldFetchBilling());
  dispatch(changeServiceContract(account.contract?.contractId));
  dispatch(resetCases());

  mixpanel.track('Switched Service Contracts', { 'New master id': masterId });
  mixpanel.identify(userInfo.emailAddress);
  const newContract = account.contract ? account.contract : {};
  const updatedProperties = {
    Address: `${account.address} ${account.city}, ${account.state} ${account.zip} ${account.country}`,
    'Agreement type': newContract.agreementType,
    Battery: newContract.brightBox,
    'Billing Method': account.billingMethod,
    'Current Owner': account.isCurrentOwner,
    'Install Partner': newContract.installPartnerName,
    'Master Id': masterId,
    'Oppty ID': account.opptyId,
    'Project Phase': newContract.status,
    'Proposal ID': newContract.proposalId,
    'Prospect ID': account.prospectId,
    'PTO Age': `${PTOAge(userInfo.contract?.ptoDate, system.get('timezone'))} days`,
    'PTO Date': newContract.ptoDate,
    'Purchased Thru': newContract.purchasedThru,
    'Service Contract Number': account.contract?.id,
    'System Count': userInfo.opportunities?.length,
    'Utility Company': newContract.utilityCompany,
  };

  // mixpanel.register - used to update values that may change over time
  mixpanel.register(updatedProperties);
};

// SELECTORS
const appSelector = state => state.app;

export const getSideDrawerOpen = createSelector([appSelector], app => app?.get('sideDrawerOpen') || false);

export const getInitalChartType = createSelector([appSelector], app => app.get('initialChartType'));

export const getMobileRatingPromptDate = createSelector([appSelector], app => app.get('mobileRatingPromptDate'));

export const getViewedAdvancedGraphs = createSelector([appSelector], app => app.get('viewedAdvancedGraphs'));

// REDUCER
export default (state = initialState, action) => {
  switch (action.type) {
    case TOGGLE_SIDE_DRAWER:
      return state.set('sideDrawerOpen', !state.get('sideDrawerOpen'));
    case CHANGE_SERVICE_CONTRACT:
      return state.set('serviceContractId', action.payload);
    case SET_INITIAL_CHART_TYPE:
      return state.set('initialChartType', action.payload);
    case MOBILE_RATING_PROMPT_DATE:
      return state.set('mobileRatingPromptDate', new Date().toISOString());
    case VIEWED_ADVANCED_GRAPHS:
      return state.set('viewedAdvancedGraphs', true);
    case RESET_VIEWED_ADVANCED_GRAPHS:
      return state.set('viewedAdvancedGraphs', false);
    case CHANGE_DEFAULT_PROSPECT:
      return state.set('defaultProspectId', action.payload);
    default:
      return state;
  }
};
